import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { UserPlus, Search, Shield, Users, Lock, AlertCircle } from 'lucide-react'
import { Skeleton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useClientProject } from '@/contexts'
import { getUserAccess, listClientUsers } from '@/services/userManagementService'
import type { AccessGrant, AccessLevel, ManagedUser } from '@/types'
import { isGlobalAdmin } from '@/types'
import { useAdminScope } from '../-admin/adminScope'
import { AddUserModal } from '../-users/AddUserModal'
import { GlobalAccessView } from '../-users/GlobalAccessView'
import { ManageAccessModal } from '../-users/ManageAccessModal'
import { accessBadgeClass, userDisplayName, userInitials } from '../-users/userDisplay'
import { PageHeader } from '@/components/page/PageHeader'
import '@/styles/pages.css'
import '../-users/accessTable.css'

export const Route = createFileRoute('/_app/organization/admin/access')({
  head: () => ({ meta: [{ title: 'Access · Admin · AERRIS' }] }),
  component: AccessTab,
})

/**
 * The Access tab has two forms. In global-default mode (platform admin, no client selected) it
 * manages every user in the system; otherwise it manages the members of the selected client. The
 * shell remounts this component when the scope flips (its outlet is keyed on the scope).
 */
function AccessTab() {
  const { globalMode, isGlobalAdmin: isPlatformAdmin } = useAdminScope()
  if (globalMode && isPlatformAdmin) {
    return (
      <div className="access-page">
        <PageHeader title="Access" subtitle="Every user on the platform, and what each one can reach." />
        <GlobalAccessView />
      </div>
    )
  }
  return <ClientAccessView />
}

function ClientAccessView() {
  // Per-client membership management — binds to the Client the shell selected (top nav),
  // and reuses the shell's resolved ADMIN gate rather than re-fetching it.
  const { clientId, selectedClient, canManage, accessLevel, loading: scopeLoading } = useAdminScope()
  const { allProjects, loadProjectsForClient } = useClientProject()

  useEffect(() => {
    if (clientId) loadProjectsForClient(clientId)
  }, [clientId, loadProjectsForClient])

  const [members, setMembers] = useState<ManagedUser[]>([])
  const [accessByUser, setAccessByUser] = useState<Map<string, AccessGrant[]>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false)
  const [manageUser, setManageUser] = useState<ManagedUser | null>(null)

  const clientName = selectedClient?.name ?? 'this client'
  const clientProjects = useMemo(
    () => allProjects.filter((p) => p.clientUuid === clientId).map((p) => ({ uuid: p.uuid, name: p.name })),
    [allProjects, clientId],
  )
  const projectsByUuid = useMemo(() => new Map(clientProjects.map((p) => [p.uuid, p.name])), [clientProjects])

  const loadToken = useRef(0)

  const loadMembers = useCallback(async () => {
    if (!clientId || !canManage) {
      setMembers([])
      setAccessByUser(new Map())
      return
    }
    const token = ++loadToken.current
    const stale = () => token !== loadToken.current
    setLoading(true)
    setError(null)
    try {
      const list = await listClientUsers(clientId)
      if (stale()) return
      setMembers(list)
      setLoading(false)

      const entries = await Promise.all(
        list.map(async (m) => {
          try {
            const access = await getUserAccess(m.uuid)
            return [m.uuid, access.grants ?? []] as const
          } catch {
            return [m.uuid, [] as AccessGrant[]] as const
          }
        }),
      )
      if (stale()) return
      setAccessByUser(new Map(entries))
    } catch (e) {
      console.error('Failed to load client members:', e)
      if (!stale()) {
        setError('Failed to load members for this client.')
        setLoading(false)
      }
    }
  }, [clientId, canManage])

  useEffect(() => {
    void loadMembers()
  }, [loadMembers])

  const memberClientLevel = useCallback(
    (uuid: string): AccessLevel | undefined =>
      accessByUser.get(uuid)?.find((g) => g.resourceType === 'CUSTOMER' && g.resourceUuid === clientId)?.level,
    [accessByUser, clientId],
  )

  const memberProjectNames = useCallback(
    (uuid: string): string[] => {
      const grants = accessByUser.get(uuid)
      if (!grants) return []
      return grants
        .filter((g) => g.resourceType === 'PROJECT' && projectsByUuid.has(g.resourceUuid))
        .map((g) => projectsByUuid.get(g.resourceUuid) as string)
    },
    [accessByUser, projectsByUuid],
  )

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return members
    return members.filter(
      (m) => userDisplayName(m).toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q),
    )
  }, [members, search])

  return (
    <div className="access-page">
      <PageHeader
        title="Access"
        subtitle={`Who can see and change ${clientName}'s contracts, and at what level.`}
        actions={
          <button
            type="button"
            className="page-btn page-btn--primary"
            onClick={openAdd}
            disabled={!clientId || !canManage}
            title={canManage ? 'Add a user to this client' : 'You need admin access to this client'}
          >
            <UserPlus size={16} />
            Add user
          </button>
        }
      />

      <section className="page-card access-card">
        <div className="access-card__head">
          <h2 className="access-card__title">
            Team members{canManage && members.length > 0 ? ` (${members.length})` : ''}
          </h2>
          {canManage && members.length > 0 && (
            <div className="access-search">
              <Search size={16} aria-hidden />
              <input
                type="text"
                placeholder="Search users…"
                aria-label="Search users"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
          )}
        </div>
        {renderBody()}
      </section>

      {clientId && canManage && (
        <AddUserModal
          opened={addOpened}
          onClose={closeAdd}
          clientUuid={clientId}
          clientName={clientName}
          onCreated={loadMembers}
        />
      )}

      <ManageAccessModal
        user={manageUser}
        clientUuid={clientId ?? ''}
        clientName={clientName}
        clientProjects={clientProjects}
        canManage={canManage}
        onClose={() => setManageUser(null)}
        onChanged={loadMembers}
      />
    </div>
  )

  function renderBody() {
    if (scopeLoading || loading) {
      return (
        <div className="access-body" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={44} radius="sm" mb="xs" />
          ))}
        </div>
      )
    }
    if (!clientId) {
      return (
        <div className="notice access-body">
          <Users size={16} />
          Select a client to manage its users.
        </div>
      )
    }
    if (error) {
      return (
        <div className="notice access-body">
          <AlertCircle size={16} />
          {error}
        </div>
      )
    }
    if (!canManage) {
      return (
        <div className="notice access-body">
          <Lock size={16} />
          You need admin access to {clientName} to manage its members
          {accessLevel ? ` (your access: ${accessLevel.toLowerCase()})` : ''}.
        </div>
      )
    }
    if (members.length === 0) {
      return <div className="empty-state access-body">No members yet — use “Add user” to add the first one.</div>
    }
    if (filteredMembers.length === 0) {
      return <div className="empty-state access-body">No users match “{search}”.</div>
    }

    return (
      <div className="access-table-wrap">
        <table className="access-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Platform role</th>
              <th>Client access</th>
              <th>Projects</th>
              <th><span className="page-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map((member) => {
              const level = memberClientLevel(member.uuid)
              const projectNames = memberProjectNames(member.uuid)
              const isGlobalAdminRole = isGlobalAdmin(member.role)
              const roleLabel =
                member.role === 'SYSTEMADMIN' ? 'System admin'
                : member.role === 'ADMIN' ? 'Global admin'
                : 'User'
              return (
                <tr key={member.uuid}>
                  <td>
                    <div className="access-person">
                      <span className="access-avatar" aria-hidden>{userInitials(member)}</span>
                      <span className="access-person__text">
                        <span className="access-person__name">
                          {userDisplayName(member)}
                          <span
                            className={`status-dot ${member.status === 'ACTIVE' ? 'active' : 'pending'}`}
                            title={member.status}
                          />
                        </span>
                        <span className="access-person__email">{member.email}</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`access-role${isGlobalAdminRole ? ' access-role--admin' : ''}`}>
                      {isGlobalAdminRole && <Shield size={13} aria-hidden />}
                      {roleLabel}
                    </span>
                  </td>
                  <td>{level ? <span className={accessBadgeClass(level)}>{level}</span> : <span className="access-muted">—</span>}</td>
                  <td>
                    {projectNames.length === 0 ? (
                      <span className="access-muted">Client-wide</span>
                    ) : (
                      <span title={projectNames.join('\n')}>
                        {projectNames.length <= 2
                          ? projectNames.join(', ')
                          : `${projectNames[0]}, +${projectNames.length - 1} more`}
                      </span>
                    )}
                  </td>
                  <td className="access-actions">
                    <button type="button" className="page-btn" onClick={() => setManageUser(member)}>
                      Manage access
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }
}
